import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentService } from '../services/student.service';
import { discipleshipService } from '../services/discipleship.service';
import type { Student } from '../types/student';
import type { DiscipleshipClass } from '../types/discipleship';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { StudentList } from '../components/discipleship/students/StudentList';
import { AddStudentForm } from '../components/discipleship/students/AddStudentForm';

export const DiscipleshipStudentManagementPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();

  const [discipleshipClass, setDiscipleshipClass] = useState<DiscipleshipClass | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);

  const fetchClassDetailsAndStudents = useCallback(async () => {
    if (!classId) {
      setError("잘못된 접근입니다: 과정 ID가 없습니다.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true); // Ensure loading is true at the start of fetch
      setError(null);
      
      const classDetails = await discipleshipService.getClassById(classId);
      if (!classDetails) {
        setError("해당 양육 과정을 찾을 수 없습니다.");
        setDiscipleshipClass(null);
        setStudents([]);
        setLoading(false); // Stop loading if class not found
        return;
      }
      setDiscipleshipClass(classDetails);
      
      const studentData = await studentService.getStudentsForClass(classId);
      setStudents(studentData);

    } catch (err) {
      console.error(err);
      setError("수강생 정보를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    fetchClassDetailsAndStudents();
  }, [fetchClassDetailsAndStudents]);

  const handleStudentAddedOrUpdated = () => {
    setIsAddStudentModalOpen(false); // Close modal if open
    fetchClassDetailsAndStudents(); // Refresh list for additions or updates
  };

  const handleRemoveStudent = async (studentId: string) => {
    if (!classId) return;
    if (window.confirm("정말로 이 수강생을 과정에서 제외하시겠습니까?")) {
      try {
        // setLoading(true); // setLoading is handled by fetchClassDetailsAndStudents
        await studentService.removeStudentFromClass(classId, studentId);
        alert("수강생이 제외되었습니다.");
        fetchClassDetailsAndStudents(); // 목록 새로고침
      } catch (err) {
        console.error("수강생 삭제 중 오류:", err);
        alert("수강생 제외 중 오류가 발생했습니다.");
        // setLoading(false); // If fetch fails, ensure loading is handled by fetch's finally
      }
    }
  };
  
  const getProgramDisplayName = (cls: DiscipleshipClass | null): string => {
    if (!cls) return "";
    return cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName;
  };

  if (loading && !discipleshipClass && students.length === 0) { // More precise initial loading
    return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  }

  if (error && !discipleshipClass) { // Show error prominently if class details failed to load
    return (
      <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-lg text-center">
        <p className="text-red-600 bg-red-100 p-4 rounded-md shadow mb-4">{error}</p>
        <button
          onClick={() => navigate('/discipleship')}
          className="px-4 py-1.5 bg-gray-500 text-white font-semibold rounded-lg shadow hover:bg-gray-600"
        >
          양육 관리 목록으로 돌아가기
        </button>
      </div>
    );
  }
  
  if (!classId || !discipleshipClass) {
     return ( // This case might be covered by error or initial loading, but good as a fallback
      <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-lg text-center">
        <p className="text-orange-600 bg-orange-100 p-4 rounded-md shadow mb-4">선택된 과정 정보를 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate('/discipleship')}
          className="px-4 py-1.5 bg-gray-500 text-white font-semibold rounded-lg shadow hover:bg-gray-600"
        >
          양육 관리 목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
            <span className="text-blue-600 dark:text-blue-400">{getProgramDisplayName(discipleshipClass)}</span> - 수강생 관리
          </h1>
          <p className="mt-1 text-md text-gray-600 dark:text-gray-400">
            {discipleshipClass.session} / 담당: {discipleshipClass.teacher}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-center mt-2 sm:mt-0"> {/* Updated gap-2 to gap-3 for better spacing */}
          <button
            onClick={() => navigate('/discipleship')}
            className="px-4 py-1.5 bg-btn-pink-bg hover:bg-btn-pink-hover text-slate-800 font-medium rounded-lg shadow-xl focus:outline-none focus:ring-2 focus:ring-btn-pink-bg focus:ring-opacity-50 transition duration-150 ease-in-out w-full sm:w-auto order-1 sm:order-none"
            aria-label="양육 관리 목록으로 돌아가기"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1 -ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            목록으로
          </button>
          <button
            onClick={() => setIsAddStudentModalOpen(true)}
            className="px-4 py-1.5 bg-btn-green-bg hover:bg-btn-green-hover text-white font-medium rounded-lg shadow-xl focus:outline-none focus:ring-2 focus:ring-btn-green-bg focus:ring-opacity-50 transition duration-150 ease-in-out w-full sm:w-auto order-2 sm:order-none"
            aria-label="새 수강생 추가"
          >
            + 새 수강생 추가
          </button>
        </div>
      </div>

      <div className="mt-8">
        {loading && students.length === 0 && <div className="text-center py-4"><LoadingSpinner /></div>}
        {!loading && students.length === 0 && !error && (
          <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-inner text-center text-gray-500 dark:text-gray-400">
            등록된 수강생이 없습니다. '새 수강생 추가' 버튼을 통해 수강생을 등록해주세요.
          </div>
        )}
        {!loading && error && students.length === 0 && ( // Error specific to loading students, class details might be available
             <p className="text-center text-red-500 bg-red-100 p-4 rounded-md shadow">{error}</p>
        )}
        {students.length > 0 && discipleshipClass && classId && (
          <StudentList 
            students={students} 
            onRemoveStudent={handleRemoveStudent} 
            durationInWeeks={discipleshipClass.durationInWeeks}
            classId={classId}
            onStudentDataChange={handleStudentAddedOrUpdated} 
          />
        )}
      </div>

      <Modal
        isOpen={isAddStudentModalOpen}
        onClose={() => setIsAddStudentModalOpen(false)}
        title="새 수강생 추가"
      >
        {classId && <AddStudentForm classId={classId} onSuccess={handleStudentAddedOrUpdated} />}
      </Modal>
    </div>
  );
};
